import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Card,
  CardContent,
  Grid,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  Stack
} from '@mui/material';
import { collection, query, where, onSnapshot, updateDoc, doc } from 'firebase/firestore';
import { convertFirestoreDate } from '../utils/dateUtils';
import { db } from '../firebase';
import { Event as EventIcon, Email as EmailIcon, Business as BusinessIcon, LocationCity as LocationIcon } from '@mui/icons-material';
import { VerificationBadge } from '../components/VerificationBadge';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ResponsiveTitle from '../components/ResponsiveTitle';

import { User as BaseUser } from '../types/User';

interface User extends BaseUser {
  eventCount?: number;
}

export const UserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const usersRef = collection(db, 'users');
    const unsubscribe = onSnapshot(usersRef, (snapshot) => {
      const usersData = snapshot.docs.map(doc => {
        const userData = doc.data();
        return {
          id: doc.id,
          uid: doc.id,
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          nickname: userData.nickname || '',
          team: userData.team || '',
          city: userData.city || '',
          role: userData.role || 'bronze',
          verified: userData.verified || false,
          createdAt: convertFirestoreDate(userData.createdAt, new Date())?.toISOString() || new Date().toISOString()
        } as User;
      });
      setUsers(usersData);
    });

    return () => unsubscribe();
  }, []);

  const handleVerificationChange = async (userId: string, newStatus: boolean) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        verified: newStatus
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
    }
  };

  const MobileView = () => (
    <Grid container spacing={2}>
      {users.map((user) => (
        <Grid item xs={12} key={user.uid}>
          <Card sx={{ bgcolor: '#333', color: '#fff' }}>
            <CardContent>
              <Stack spacing={2}>
                {/* En-tête avec nom et statut */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" component="div">
                    {user.firstName} {user.lastName}
                    {user.nickname && (
                      <Typography variant="caption" sx={{ color: '#aaa', display: 'block' }}>
                        {user.nickname}
                      </Typography>
                    )}
                  </Typography>
                  <Box>
                    <VerificationBadge
                      verified={user.verified || false}
                      userId={user.uid}
                      onStatusChange={handleVerificationChange}
                    />
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

                {/* Informations de contact et détails */}
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" />
                    <Typography>{user.email}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="small" />
                    <Typography>{user.team || '-'}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" />
                    <Typography>{user.city || '-'}</Typography>
                  </Box>
                </Stack>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

                {/* Pied de carte avec événements et date */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Badge badgeContent={user.eventCount || 0} color="primary">
                    <EventIcon />
                  </Badge>
                  <Typography variant="caption" sx={{ color: '#aaa' }}>
                    Inscrit le {format(user.createdAt, "dd/MM/yyyy", { locale: fr })}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const DesktopView = () => (
    <TableContainer component={Paper} sx={{ bgcolor: '#333' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: '#fff' }}>Utilisateur</TableCell>
            <TableCell sx={{ color: '#fff' }}>Contact</TableCell>
            <TableCell sx={{ color: '#fff' }}>Équipe</TableCell>
            <TableCell sx={{ color: '#fff' }}>Ville</TableCell>
            <TableCell align="center" sx={{ color: '#fff' }}>Événements</TableCell>
            <TableCell align="center" sx={{ color: '#fff' }}>Statut</TableCell>
            <TableCell sx={{ color: '#fff' }}>Inscription</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.uid}>
              <TableCell sx={{ color: '#fff' }}>
                {user.firstName} {user.lastName}
                {user.nickname && (
                  <Typography variant="caption" sx={{ color: '#aaa', display: 'block' }}>
                    {user.nickname}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ color: '#fff' }}>{user.email}</TableCell>
              <TableCell sx={{ color: '#fff' }}>{user.team || '-'}</TableCell>
              <TableCell sx={{ color: '#fff' }}>{user.city || '-'}</TableCell>
              <TableCell align="center" sx={{ color: '#fff' }}>
                <Badge badgeContent={user.eventCount || 0} color="primary">
                  <EventIcon />
                </Badge>
              </TableCell>
              <TableCell align="center">
                <VerificationBadge
                  verified={user.verified || false}
                  userId={user.uid}
                  onStatusChange={handleVerificationChange}
                />
              </TableCell>
              <TableCell sx={{ color: '#fff' }}>
                {format(user.createdAt, "dd/MM/yyyy", { locale: fr })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <ResponsiveTitle variant="h4" gutterBottom sx={{ color: '#fff', mb: 4 }}>
        Gestion des utilisateurs
      </ResponsiveTitle>

      {isMobile ? <MobileView /> : <DesktopView />}
    </Container>
  );
};
